// frontend/src/modules/workspace/pages/AccessPage.tsx
//
// Tenant-side Access management. Two tabs:
//   · Members            — table of workspace members with role/status/last-login,
//                          row actions (change roles, deactivate/reactivate,
//                          resend invite) and an invite-member dialog.
//   · Roles & permissions — system roles (read-only) + custom roles with a
//                          permission editor grouped by module.
//
// Mirrors the platform-side AdminRolesPage structure/UX. Backed by
// /api/workspace (requireTenantUser + requirePermission('access.manage')).
// A 403 on the members fetch renders a friendly empty state instead of
// crashing.

import * as React from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck, UsersRound, UserPlus, Lock, Plus, Pencil, Trash2, ScrollText,
} from 'lucide-react';
import { ApiError } from '../../../core/api-client';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { formatDateTime } from '../../../design-system/format';
import { workspaceApi } from '../api';
import type {
  WorkspaceMember, WorkspaceRole, WorkspacePermission, WorkspaceMemberStatus, AuditEntry,
} from '../types';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { InviteMemberDialog } from '../components/InviteMemberDialog';
import { ChangeRolesDialog } from '../components/ChangeRolesDialog';
import { RoleEditorDialog } from '../components/RoleEditorDialog';

const MODULE_LABELS: Record<string, string> = {
  access:     'Access',
  invoicing:  'Invoicing',
  payments:   'Payments',
  expenses:   'Expenses',
  hr:         'HR & People',
  payroll:    'Payroll',
  projects:   'Projects',
  monitoring: 'Monitoring',
  email:      'Email',
};

const STATUS_TONE: Record<WorkspaceMemberStatus, StatusTone> = {
  active:   'success',
  pending:  'warning',
  inactive: 'muted',
};
const STATUS_LABEL: Record<WorkspaceMemberStatus, string> = {
  active:   'Active',
  pending:  'Pending',
  inactive: 'Inactive',
};

export function AccessPage() {
  const confirm = useConfirm();
  const [members, setMembers] = React.useState<WorkspaceMember[] | null>(null);
  const [roles, setRoles] = React.useState<WorkspaceRole[] | null>(null);
  const [permissions, setPermissions] = React.useState<WorkspacePermission[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [forbidden, setForbidden] = React.useState(false);

  // Dialog state.
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [rolesFor, setRolesFor] = React.useState<WorkspaceMember | null>(null);
  const [roleEditor, setRoleEditor] = React.useState<{ role: WorkspaceRole | null } | null>(null);

  // Audit log — loaded lazily the first time the Audit tab is opened.
  const [audit, setAudit] = React.useState<AuditEntry[] | null>(null);
  const [auditError, setAuditError] = React.useState<string | null>(null);
  const [auditLoading, setAuditLoading] = React.useState(false);

  const loadAudit = React.useCallback(() => {
    setAuditError(null);
    setAuditLoading(true);
    workspaceApi.listAudit()
      .then(setAudit)
      .catch((e) => setAuditError(String(e?.message ?? e)))
      .finally(() => setAuditLoading(false));
  }, []);

  const load = React.useCallback(() => {
    setError(null);
    setForbidden(false);
    setMembers(null);
    setRoles(null);
    setPermissions(null);

    Promise.all([
      workspaceApi.listMembers(),
      workspaceApi.listRoles(),
      workspaceApi.listPermissions(),
    ])
      .then(([m, r, p]) => {
        setMembers(m);
        setRoles(r);
        setPermissions(p);
      })
      .catch((e) => {
        if (e instanceof ApiError && (e.code === 'forbidden' || e.status === 403)) {
          setForbidden(true);
        } else {
          setError(String(e?.message ?? e));
        }
      });
  }, []);

  React.useEffect(() => { load(); }, [load]);

  function refreshMembers() {
    workspaceApi.listMembers().then(setMembers).catch(() => { /* keep current */ });
  }
  function refreshRoles() {
    // Roles list carries member_count/permissions — re-pull after edits.
    workspaceApi.listRoles().then(setRoles).catch(() => { /* keep current */ });
  }

  function applyMember(updated: WorkspaceMember) {
    setMembers((prev) => (prev ? prev.map((m) => (m.id === updated.id ? updated : m)) : prev));
  }

  async function toggleActive(member: WorkspaceMember) {
    const next = !member.is_active;
    const verb = next ? 'Reactivate' : 'Deactivate';
    if (!(await confirm({ title: `${verb} ${member.name || member.email}?`, description: next ? undefined : "They won't be able to sign in.", destructive: !next, confirmLabel: verb }))) return;
    try {
      const updated = await workspaceApi.setMemberActive(member.id, next);
      applyMember(updated);
      toast.success(next ? 'Member reactivated' : 'Member deactivated');
    } catch (err: any) {
      const msg =
        err?.code === 'last_owner'      ? 'This workspace must keep at least one active owner.' :
        err?.code === 'self_deactivate' ? "You can't deactivate your own account." :
        (err.message ?? String(err));
      toast.error('Update failed', { description: msg });
    }
  }

  async function resendInvite(member: WorkspaceMember) {
    try {
      const r = await workspaceApi.resendInvite(member.id);
      toast.success('Invite resent', {
        description: `Magic link expires ${formatDateTime(r.magic_link_expires_at)}.`,
      });
    } catch (err: any) {
      const msg = err?.code === 'already_active'
        ? 'This member has already activated their account.'
        : (err.message ?? String(err));
      toast.error('Could not resend', { description: msg });
    }
  }

  async function deleteRole(role: WorkspaceRole) {
    if (!(await confirm({ title: `Delete the "${role.name}" role?`, description: 'This cannot be undone.', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await workspaceApi.deleteRole(role.id);
      toast.success('Role deleted');
      refreshRoles();
    } catch (err: any) {
      const msg =
        err?.code === 'system_role' ? 'System roles cannot be deleted.' :
        err?.code === 'role_in_use' ? 'This role is still assigned to members. Reassign them first.' :
        (err.message ?? String(err));
      toast.error('Delete failed', { description: msg });
    }
  }

  if (forbidden) {
    return (
      <div>
        <PageHeader eyebrow="Workspace / Access" title="Access" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <div className={[VISUAL.bento, 'p-2'].join(' ')}>
            <EmptyState
              icon={<Lock />}
              tone="muted"
              title="You don't have access to manage members"
              description="Ask a workspace owner to grant you the access.manage permission to view and manage members and roles."
            />
          </div>
        </div>
      </div>
    );
  }

  if (error) return <ErrorState description={error} onRetry={load} />;
  if (!members || !roles || !permissions) return <LoadingState variant="page" />;

  return (
    <div>
      <PageHeader
        eyebrow="Workspace / Access"
        title="Access"
        description="Manage who can sign in to this workspace and what each person is allowed to do."
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-14">
        <Tabs
          defaultValue="members"
          onValueChange={(v) => { if (v === 'audit' && audit === null && !auditLoading) loadAudit(); }}
        >
          <TabsList className="mb-6">
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="roles">Roles &amp; permissions</TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
          </TabsList>

          {/* ─────────────── MEMBERS ─────────────── */}
          <TabsContent value="members">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-lavender-100 ring-1 ring-lavender-200 dark:bg-lavender-400/15 dark:ring-lavender-400/25">
                  <UsersRound className="h-4 w-4 text-lavender-700 dark:text-lavender-200" strokeWidth={1.7} />
                </div>
                <h3 className={VISUAL.dashSection}>Members</h3>
              </div>
              <Button data-tour="team.invite" onClick={() => setInviteOpen(true)} className="gap-1.5">
                <UserPlus className="h-3.5 w-3.5" />
                Invite member
              </Button>
            </div>

            {members.length === 0 ? (
              <div className={[VISUAL.bento, 'p-2'].join(' ')}>
                <EmptyState
                  icon={<UsersRound />}
                  title="No members yet"
                  description="Invite your first teammate to give them access to this workspace."
                  action={<Button onClick={() => setInviteOpen(true)} className="gap-1.5"><UserPlus className="h-3.5 w-3.5" />Invite member</Button>}
                />
              </div>
            ) : (
              <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                <ul className="divide-y divide-border/40">
                  {members.map((m) => (
                    <li key={m.id} className="flex items-center gap-4 px-5 sm:px-6 py-4">
                      <div className="h-10 w-10 rounded-full bg-coral text-white flex items-center justify-center font-bold text-[13px] ring-1 ring-coral/30 shrink-0">
                        {(m.name || m.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-semibold text-foreground truncate">{m.name || m.email}</p>
                        <p className="text-[12px] text-muted-foreground truncate">{m.email}</p>
                      </div>

                      {/* Role chips */}
                      <div className="hidden md:flex flex-wrap gap-1.5 max-w-[18rem] justify-end">
                        {m.roles.length === 0 ? (
                          <span className={statusPill('draft')}>no roles</span>
                        ) : (
                          m.roles.map((r) => (
                            <span key={r.id} className={statusPill(r.is_system ? 'sent' : 'info')}>{r.name}</span>
                          ))
                        )}
                      </div>

                      {/* Last login */}
                      <div className="hidden lg:block w-40 text-right">
                        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70">Last login</p>
                        <p className="text-[12.5px] text-foreground/80 tabular-nums">
                          {m.last_login_at ? formatDateTime(m.last_login_at) : '—'}
                        </p>
                      </div>

                      {/* Status */}
                      <span className={statusPill(STATUS_TONE[m.status])}>
                        <span className={statusDot(STATUS_TONE[m.status])} />
                        {STATUS_LABEL[m.status]}
                      </span>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => setRolesFor(m)}>
                          Change roles
                        </Button>
                        {m.status === 'pending' && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => resendInvite(m)}>
                            Resend invite
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(m)}
                          className={m.is_active ? 'text-rose-600 hover:text-rose-700 dark:text-rose-300' : ''}
                        >
                          {m.is_active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          {/* ─────────────── ROLES & PERMISSIONS ─────────────── */}
          <TabsContent value="roles">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
                  <ShieldCheck className="h-4 w-4 text-coral" strokeWidth={1.7} />
                </div>
                <h3 className={VISUAL.dashSection}>Roles &amp; permissions</h3>
              </div>
              <Button onClick={() => setRoleEditor({ role: null })} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Create role
              </Button>
            </div>

            <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {roles.map((role) => (
                <li key={role.id} className={[VISUAL.bento, 'p-6'].join(' ')}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[15px] font-semibold tracking-[-0.005em] text-foreground">{role.name}</p>
                        {role.is_system && <span className={statusPill('sent')}>system</span>}
                      </div>
                      <p className="text-[12px] font-mono text-muted-foreground mt-0.5">{role.key}</p>
                    </div>
                    <span className={statusPill('info')}>
                      {role.permissions.length} {role.permissions.length === 1 ? 'permission' : 'permissions'}
                    </span>
                  </div>

                  {role.description && (
                    <p className="mt-3 text-[13px] text-muted-foreground leading-relaxed">{role.description}</p>
                  )}

                  <p className="mt-3 text-[12px] text-muted-foreground">
                    {role.member_count} {role.member_count === 1 ? 'member' : 'members'}
                  </p>

                  {/* Permission chips */}
                  {role.permissions.length > 0 && (
                    <ul className="mt-4 flex flex-wrap gap-1.5">
                      {role.permissions.map((perm) => (
                        <li
                          key={perm}
                          className="inline-flex items-center px-2.5 py-1 rounded-full bg-muted/60 text-[11.5px] font-mono text-foreground/80 ring-1 ring-border/40"
                        >
                          {perm}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Actions — only custom roles are editable */}
                  {!role.is_system && (
                    <div className="mt-5 flex items-center gap-1 pt-4 border-t border-border/40">
                      <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => setRoleEditor({ role })}>
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-rose-600 hover:text-rose-700 dark:text-rose-300"
                        onClick={() => deleteRole(role)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* ─────────────── AUDIT ─────────────── */}
          <TabsContent value="audit">
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-saffron/15 ring-1 ring-saffron/25">
                <ScrollText className="h-4 w-4 text-saffron" strokeWidth={1.7} />
              </div>
              <div>
                <h3 className={VISUAL.dashSection}>Audit log</h3>
                <p className="text-[12.5px] text-muted-foreground">
                  A read-only trail of member and role changes in this workspace.
                </p>
              </div>
            </div>

            {auditError ? (
              <ErrorState description={auditError} onRetry={loadAudit} />
            ) : audit === null || auditLoading ? (
              <LoadingState variant="inline" />
            ) : audit.length === 0 ? (
              <div className={[VISUAL.bento, 'p-2'].join(' ')}>
                <EmptyState
                  icon={<ScrollText />}
                  tone="muted"
                  title="No audit activity yet"
                  description="Member invites, role changes, and deactivations will appear here as they happen."
                />
              </div>
            ) : (
              <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                <ul className="divide-y divide-border/40">
                  {audit.map((entry) => (
                    <li key={entry.id} className="flex items-start gap-4 px-5 sm:px-6 py-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={statusPill('info')}>{entry.action}</span>
                          <p className="text-[14px] font-medium text-foreground truncate">{entry.summary}</p>
                        </div>
                        <p className="text-[12px] text-muted-foreground mt-1">
                          {entry.actor_label ?? 'Unknown'}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-[12.5px] text-foreground/80 tabular-nums">{formatDateTime(entry.created_at)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        roles={roles}
        onInvited={refreshMembers}
      />
      <ChangeRolesDialog
        open={rolesFor !== null}
        onOpenChange={(v) => { if (!v) setRolesFor(null); }}
        member={rolesFor}
        roles={roles}
        onSaved={(updated) => { applyMember(updated); refreshRoles(); }}
      />
      <RoleEditorDialog
        open={roleEditor !== null}
        onOpenChange={(v) => { if (!v) setRoleEditor(null); }}
        role={roleEditor?.role ?? null}
        permissions={permissions}
        moduleLabels={MODULE_LABELS}
        onSaved={refreshRoles}
      />
    </div>
  );
}
